import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { fieldStore } from '../../utils/fieldBatches';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { photoTypeLabel } from '../../utils/photoCapture';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import * as S from '../specimens/styles';

/*
 * The Field gallery, to attach from (docs/FIELD_AI_VISION_SPEC.md 32):
 *
 *   - a SENT record is shared by reference (`record_ref`): each reader then
 *     sees it through their own rules, and never a copy of it;
 *   - a photograph of a batch still on this device (not sent yet, so the
 *     server has no record to point at) can only go as a copy -- an `image`
 *     -- and says so: "Sends a copy. The record itself is not shared."
 *
 * mode "place" lists only the sent records, to send one's place: the record
 * is read (GET /collections/{id}) for its coordinates, which its collector
 * may see.
 *
 * onPickRecord({sample_id, label, thumbnail}), onPickPlace({lat, lng, label}),
 * onPickCopies([Blob]) answer the choice; the dialog then closes.
 */
const RECENT = 24;

export default function GalleryPicker({ me, mode = 'attach', onPickRecord, onPickPlace, onPickCopies, onClose }) {
  const { t } = useTranslation('chat');
  const [records, setRecords] = useState(null);
  const [batches, setBatches] = useState([]);
  const [chosen, setChosen] = useState([]);   // [{batchId, photoId}] of unsent photographs
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.get('/collections', { params: { mine: 1, limit: RECENT } })
      .then((r) => { if (live) setRecords(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (live) { setRecords([]); setError(errorText(e)); } });
    if (mode === 'attach' && me) {
      fieldStore().list(me)
        .then((all) => { if (live) setBatches(all.filter((b) => !b.serverSampleId && (b.photos || []).length)); })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [me, mode]);

  const label = (r) => [r.code || r.qr_uid, r.current_taxon_name].filter(Boolean).join(' · ');

  const pickRecord = async (r) => {
    if (mode === 'attach') {
      onPickRecord?.({ sample_id: r.sample_id, label: label(r), thumbnail: r.thumbnail_url || null });
      onClose();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const full = (await api.get(`/collections/${r.sample_id}`)).data;
      const lat = Number(full.gps_lat);
      const lng = Number(full.gps_lng);
      if (full.gps_lat == null || full.gps_lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        setError(t('attach.noPlace'));
        return;
      }
      onPickPlace?.({ lat, lng, label: label(r) });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (batchId, photoId) => setChosen((list) => (
    list.some((c) => c.photoId === photoId) ? list.filter((c) => c.photoId !== photoId)
      : [...list, { batchId, photoId }]));

  const sendCopies = async () => {
    setBusy(true);
    try {
      const blobs = (await Promise.all(chosen.map((c) => fieldStore().getBlob(me, c.photoId)))).filter(Boolean);
      onPickCopies?.(blobs);
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={mode === 'place' ? t('attach.placeOfRecord') : t('attach.fromGallery')} onClose={onClose}
      busy={busy} testId="chat-gallery">
      <h3 style={heading}>{t('attach.sentRecords')}</h3>
      <p style={S.hint}>{mode === 'place' ? t('attach.placeHint') : t('attach.referenceHint')}</p>
      {records === null && <p style={S.hint}>{t('loading')}</p>}
      {records && records.length === 0 && <p style={S.hint}>{t('attach.noRecords')}</p>}
      <ul style={grid}>
        {(records || []).map((r) => (
          <li key={r.sample_id}>
            <button type="button" style={tile} disabled={busy} onClick={() => pickRecord(r)}
              data-testid="chat-gallery-record">
              {r.thumbnail_url
                ? <img src={resolvePhotoUrl(r.thumbnail_url)} alt="" style={tileImage} loading="lazy" />
                : <span style={{ ...tileImage, display: 'grid', placeItems: 'center' }} aria-hidden="true">🍄</span>}
              <span style={tileText} dir="auto">{label(r) || t('attach.aCollection')}</span>
            </button>
          </li>
        ))}
      </ul>
      {mode === 'attach' && batches.length > 0 && (
        <>
          <h3 style={heading}>{t('attach.onDevice')}</h3>
          <p style={S.hint} data-testid="chat-gallery-copy-notice">{t('attach.copyNotice')}</p>
          {batches.map((b) => (
            <ul key={b.id} style={grid}>
              {(b.photos || []).map((p) => (
                <DevicePhoto key={p.id} me={me} photo={p} chosen={chosen.some((c) => c.photoId === p.id)}
                  onToggle={() => toggle(b.id, p.id)} />
              ))}
            </ul>
          ))}
        </>
      )}
      {error && <p role="alert" style={S.error}>{error}</p>}
      <ActionBar>
        <Button onClick={onClose} disabled={busy}>{t('cancel')}</Button>
        {mode === 'attach' && batches.length > 0 && (
          <Button variant="primary" disabled={busy || !chosen.length} onClick={sendCopies}
            data-testid="chat-gallery-copies">{t('attach.addCopies', { count: chosen.length })}</Button>
        )}
      </ActionBar>
    </Dialog>
  );
}

function DevicePhoto({ me, photo, chosen, onToggle }) {
  const { t } = useTranslation(['chat', 'common']);
  const [url, setUrl] = useState('');
  useEffect(() => {
    let made = '';
    let live = true;
    fieldStore().getBlob(me, photo.id).then((blob) => {
      if (!live || !blob || typeof URL.createObjectURL !== 'function') return;
      made = URL.createObjectURL(blob);
      setUrl(made);
    }).catch(() => {});
    return () => {
      live = false;
      if (made) URL.revokeObjectURL?.(made);
    };
  }, [me, photo.id]);
  return (
    <li>
      <button type="button" style={{ ...tile, ...(chosen ? tileOn : {}) }} aria-pressed={chosen} onClick={onToggle}
        data-testid="chat-gallery-device-photo">
        {url ? <img src={url} alt="" style={tileImage} /> : <span style={tileImage} aria-hidden="true" />}
        <span style={tileText}>{photoTypeLabel(t, photo.view)}</span>
      </button>
    </li>
  );
}

const heading = { margin: '12px 0 4px', fontSize: 15 };
const grid = {
  listStyle: 'none', margin: '8px 0', padding: 0, display: 'grid', gap: 8,
  gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
};
const tile = {
  width: '100%', display: 'flex', flexDirection: 'column', gap: 4, padding: 4, cursor: 'pointer',
  border: '1px solid var(--c-border)', borderRadius: 8, background: 'var(--c-surface)', color: 'var(--c-text-fg)',
  font: 'inherit',
};
const tileOn = { borderColor: 'var(--c-primary)', boxShadow: '0 0 0 2px var(--c-primary)' };
const tileImage = { width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 6, background: 'var(--c-bg-subtle)' };
const tileText = { fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
